import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { getPopupList } from '@/service/common.service'
import { toUserFriendlyMessage } from '@cnd_fe/ky-client'

/**
 * 팝업 전역 관리 composable
 * @returns {Object} 팝업 관련 상태 및 메서드
 */
export function usePopup() {
  const route = useRoute()
  const popups = ref([])
  const isLoading = ref(false)
  const error = ref(null)

  /**
   * 게시 기간 확인
   * @param {string} bgnde - 시작일시 (yyyyMMddHHmm)
   * @param {string} endde - 종료일시 (yyyyMMddHHmm)
   * @returns {boolean} 게시 기간 내인 경우 true
   */
  const isWithinDisplayPeriod = (bgnde, endde) => {
    const now = new Date()
    const nowStr = now.getFullYear() + 
      String(now.getMonth() + 1).padStart(2, '0') + 
      String(now.getDate()).padStart(2, '0') + 
      String(now.getHours()).padStart(2, '0') + 
      String(now.getMinutes()).padStart(2, '0')
    
    const trimmedBgnde = (bgnde || '').trim()
    const trimmedEndde = (endde || '').trim()
    
    return nowStr >= trimmedBgnde && nowStr <= trimmedEndde
  }

  /**
   * 팝업 데이터 변환
   * @param {Object} popup - 원본 팝업 데이터
   * @returns {Object} 변환된 팝업 데이터
   */
  const transformPopup = (popup) => {
    const { contType, nclHtml, embededHtml, fileUrl, dspType, popupVrticlLc, popupWidthLc, popupVrticlSize, popupWidthSize, stopvewSetupAt, popupSjNm } = popup
    
    const variant = dspType === '2' || dspType === 2 ? 'layer' : 'window'
    
    return {
      ...popup,
      // contType: 1=HTML, 2=URL
      contentHtml: contType === '1' || contType === 1 ? (embededHtml || nclHtml || '') : '',
      contentUrl: contType === '2' || contType === 2 ? (fileUrl || '') : '',
      // dspType: 1=Window(window.open), 2=Layer(모달/팝업창)
      variant: variant,
      // 위치와 크기 (좌표: left/top, 크기: width/height)
      // popupWidthLc: 가로 좌표 (x축) → left
      // popupVrticlLc: 세로 좌표 (y축) → top
      left: Number.isFinite(parseInt(popupWidthLc)) ? parseInt(popupWidthLc) : 0,
      top: Number.isFinite(parseInt(popupVrticlLc)) ? parseInt(popupVrticlLc) : 0,
      // popupWidthSize: 가로 크기 (너비)
      // popupVrticlSize: 세로 크기 (높이)
      width: parseInt(popupWidthSize) || 800,
      height: parseInt(popupVrticlSize) || 600,
      // 기타 설정
      title: popupSjNm || '',
      stopVewAt: stopvewSetupAt === 'Y' ? 'Y' : 'N',
      visible: true
    }
  }

  /**
   * 팝업 목록 조회
   * @param {Object} options - 조회 옵션
   * @param {string} [options.menuId] - 메뉴ID
   * @param {string} [options.domainId] - 도메인ID
   */
  const fetchPopups = async (options = {}) => {
    const { menuId, domainId, instId } = options

    if (!menuId && !domainId && !instId) {
      popups.value = []
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const res = await getPopupList({
        ...(menuId && { menuId }),
        ...(domainId && { domainId }),
        ...(instId && { instId })
      })
      if (res && res.data) {
        const popupList = Array.isArray(res.data) ? res.data : [res.data]
        // 게시 기간에 맞춰 필터링
        popups.value = filterPopups(popupList)
      } else {
        popups.value = []
      }
    } catch (err) {
      error.value = toUserFriendlyMessage(err?.serverMessage || err?.message)
      popups.value = []
    } finally {
      isLoading.value = false
    }
  }

  /**
   * 팝업 닫기
   * @param {string} popupId - 팝업ID
   */
  const closePopup = (popupId) => {
    popups.value = popups.value.filter(p => p.popupId !== popupId)
  }

  /**
   * 팝업 필터링 및 변환 (게시 기간 확인)
   * @param {Array} popupList - 팝업 목록
   * @returns {Array} 필터링 및 변환된 팝업 목록
   */
  const filterPopups = (popupList) => {
    return (popupList || [])
      .filter(popup => isWithinDisplayPeriod(popup.ntceBgnde, popup.ntceEndde))
      .map(popup => transformPopup(popup))
  }

  /**
   * 모든 팝업 닫기
   */
  const closeAllPopups = () => {
    popups.value = []
  }

  /**
   * 팝업 표시 여부
   */
  const hasPopups = computed(() => popups.value.length > 0)

  return {
    popups,
    isLoading,
    error,
    hasPopups,
    fetchPopups,
    closePopup,
    closeAllPopups,
    isWithinDisplayPeriod,
    filterPopups,
    transformPopup
  }
}
